import {
  useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState,
  type FocusEvent, type FormEvent, type KeyboardEvent, type MouseEvent,
} from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../auth/useAuth";
import { ga4 } from "../analytics/ga4";
import { submitContactRequest, isValidEmail, isValidPhone, type ContactSource } from "../lib/contactValidation";
import { loadCatalog, retryCatalog, useCatalog, type Catalog, type CatalogStatus } from "./catalog";
import { composeMessage, toComposeInput, type ComposeResult, type Translate } from "./composeMessage";
import { CATALOG_INTENTS, stepsFor, type IntentId, type StepId } from "./ids";
import { isoDay, nowDate, seasonFor } from "./season";
import {
  composerReducer, initialState,
  type Action, type ComposerState, type FieldErrors, type FieldName, type StampView,
} from "./state";
import type { OpenContext } from "./context";
import { hasErrors, missingItems, validateDetails, validateFinal, validateTopic, type MissingItem } from "./validation";

/**
 * Il motore della richiesta guidata, condiviso da pagina Contatti e widget.
 *
 * Vive nel componente che NON si smonta (Contact o ContactWidget): la bozza
 * resta in memoria finché la pagina è aperta, mai in localStorage. La vista
 * (`RequestComposer`) si limita a disegnare ciò che questo hook restituisce.
 *
 * Contratto con il backend invariato: si chiama `submitContactRequest` con gli
 * stessi campi di sempre; tutte le scelte stanno nel testo di `message`.
 */

export interface ComposerOptions {
  source: ContactSource;
  /** Lingua del payload, inviata così com'è (pagina: rotta; widget: i18n.language). */
  locale: string;
  gaLocation: "contact_page" | "widget";
  surface: "page" | "panel";
  /** Pagina: dopo un cambio d'altezza ricalcola le rivelazioni allo scroll. */
  onLayoutChange?: () => void;
  /** Contesto letto all'apertura (pagina: la CTA di Collezioni apre l'offerta). */
  initialContext?: OpenContext | null;
}

type ElKey =
  | "h1" | "h2" | "h3" | "success" | "summary" | "firstIntent" | "date" | "note" | "name" | "email" | "company" | "phone"
  | "submit" | "form" | "body" | "pending";

const SEND_TIMEOUT_MS = 20000;
/** Ordine dei campi del passo finale: Invio passa al successivo, mai un invio implicito. */
const CONTACT_ORDER: ElKey[] = ["note", "name", "email", "company", "phone", "submit"];

function headingKey(step: StepId): ElKey {
  return step === 1 ? "h1" : step === 2 ? "h2" : "h3";
}
const LANGS = ["it", "en", "de"];

function normLang(locale: string): string {
  const l = (locale || "it").slice(0, 2).toLowerCase();
  return LANGS.includes(l) ? l : "en";
}

function reducedMotion(): boolean {
  try {
    return !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
  } catch {
    return true;
  }
}

/** Con movimento ridotto lo scorrimento è istantaneo (anche se html ha scroll-behavior: smooth). */
function scrollBehavior(): ScrollBehavior {
  return reducedMotion() ? "instant" : "smooth";
}

export interface ComposerApi {
  opts: ComposerOptions;
  lang: string;
  t: Translate;
  state: ComposerState;
  dispatch: (a: Action) => void;
  steps: readonly StepId[];
  stepNumber: number;
  catalog: { status: CatalogStatus; data: Catalog | null };
  composed: ComposeResult;
  emailValue: string;
  authEmail: string | null;
  missing: MissingItem[];
  errors: FieldErrors;
  stamps: StampView[];
  /** Nell'offerta la quantità complessiva si chiede solo se non ci sono confezioni scelte. */
  showOfferQtyBand: boolean;
  season: { christmas: boolean; year: number };
  todayIso: string;
  sending: boolean;
  setEl: (key: ElKey) => (el: HTMLElement | null) => void;
  chooseIntent: (id: IntentId) => void;
  writeInstead: () => void;
  next: () => void;
  back: () => void;
  goTo: (step: StepId, edit?: boolean) => void;
  prefetch: () => void;
  retryCatalog: () => void;
  applyContext: (ctx: OpenContext | null) => void;
  focusCurrent: () => void;
  fieldBlur: (field: "email" | "phone") => (e: FocusEvent<HTMLInputElement>) => void;
  changeField: (field: FieldName, value: string) => void;
  onFormKeyDown: (e: KeyboardEvent<HTMLFormElement>) => void;
  onSubmitClick: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  focusField: (key: "name" | "email" | "phone" | "note") => void;
  dismissSuccess: () => void;
  mergeCart: () => void;
  dismissCart: () => void;
}

export function useRequestComposer(opts: ComposerOptions): ComposerApi {
  const { i18n } = useTranslation();
  const auth = useContext(AuthContext);
  const authEmail = auth?.user?.email ?? null;
  const [state, dispatch] = useReducer(composerReducer, opts.initialContext ?? null, (ctx) =>
    ctx ? composerReducer(initialState(), { type: "context", ctx }) : initialState(),
  );
  const [today] = useState(nowDate);
  const lang = normLang(opts.locale);

  const t: Translate = useMemo(() => {
    const fixed = i18n.getFixedT(lang);
    return (key, vars) => String(fixed(key, vars));
  }, [i18n, lang]);

  const catalog = useCatalog(lang);
  const season = useMemo(() => seasonFor(today), [today]);
  const todayIso = useMemo(() => isoDay(today), [today]);
  const bySlug = catalog.data?.bySlug ?? null;

  const input = useMemo(() => toComposeInput(state.draft, bySlug, season.year, lang), [state.draft, bySlug, season.year, lang]);
  const composed = useMemo<ComposeResult>(() => {
    try {
      return composeMessage(input, t);
    } catch {
      // Rete di sicurezza: un dato inatteso non deve mai rompere il modulo.
      const note = state.draft.note.trim();
      return { message: note, headline: null, guided: "", noteBudget: 5000 };
    }
  }, [input, t, state.draft.note]);

  const emailValue = state.draft.email ?? authEmail ?? "";
  const finalInput = useMemo(
    () => ({ draft: state.draft, email: emailValue, message: composed.message }),
    [state.draft, emailValue, composed.message],
  );
  const missing = useMemo(() => missingItems({ ...finalInput, todayIso }), [finalInput, todayIso]);

  // Dopo il primo tentativo d'invio gli errori seguono ciò che si scrive.
  const errors: FieldErrors = useMemo(() => {
    if (!state.attempted) return state.errors;
    return { ...(state.errors.intent ? { intent: true as const } : {}), ...validateDetails(state.draft, todayIso), ...validateFinal(finalInput) };
  }, [state.attempted, state.errors, state.draft, todayIso, finalInput]);

  const steps = stepsFor(state.draft.intent);
  const stepNumber = Math.max(1, steps.indexOf(state.step) + 1);

  const stamps: StampView[] = useMemo(() => {
    const d = state.draft;
    const slugs = d.intent === "question" ? (d.box ? [d.box] : []) : d.intent && ["offer", "custom", "branding"].includes(d.intent) ? d.boxes : [];
    return slugs.flatMap((slug) => {
      const box = bySlug?.[slug];
      const title = box?.title ?? d.titles[slug];
      return title ? [{ slug, title, imageUrl: box?.imageUrl ?? "" }] : [];
    });
  }, [state.draft, bySlug]);

  const showOfferQtyBand = state.draft.intent === "offer" && input.boxes.length === 0;

  // ── riferimenti agli elementi (letti solo nei gestori e negli effetti) ──
  const els = useRef<Partial<Record<ElKey, HTMLElement | null>>>({});
  const setters = useRef<Partial<Record<ElKey, (el: HTMLElement | null) => void>>>({});
  const setEl = useCallback((key: ElKey) => {
    let fn = setters.current[key];
    if (!fn) {
      fn = (el: HTMLElement | null) => { els.current[key] = el; };
      setters.current[key] = fn;
    }
    return fn;
  }, []);

  const sendingRef = useRef(false);
  const mountedRef = useRef(true);
  const editIntentRef = useRef<IntentId | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const { onLayoutChange, surface, source, gaLocation, locale } = opts;
  useEffect(() => {
    onLayoutChange?.();
  }, [state.step, state.phase, catalog.status, onLayoutChange]);

  // Il catalogo nella lingua corrente serve se ci sono confezioni scelte o se si
  // è ai dettagli di un argomento con confezioni: copre anche il cambio di lingua
  // a metà richiesta (la cache è per lingua).
  const d0 = state.draft;
  const wantsCatalog = d0.boxes.length > 0 || d0.box !== null || (state.step === 2 && !!d0.intent && CATALOG_INTENTS.includes(d0.intent));
  useEffect(() => {
    if (wantsCatalog) loadCatalog(lang);
  }, [wantsCatalog, lang]);

  const reveal = useCallback((el: HTMLElement | null | undefined, block: ScrollLogicalPosition = "start") => {
    if (!el) return;
    el.focus({ preventScroll: true });
    if (surface === "panel") {
      // Nel pannello scorre solo il corpo: il campo va portato in vista lì.
      el.scrollIntoView?.({ block: "nearest", behavior: scrollBehavior() });
      return;
    }
    // Un campo si giudica dalla sua etichetta: l'input può essere in vista e l'etichetta sotto l'header.
    const r = (el.closest(".rq-field") ?? el).getBoundingClientRect();
    if (r.top < 96 || r.top > window.innerHeight * 0.6) {
      el.scrollIntoView?.({ block, behavior: scrollBehavior() });
    }
  }, [surface]);

  const goTo = useCallback((step: StepId, edit?: boolean) => {
    flushSync(() => dispatch({ type: "goTo", step, edit }));
    if (surface === "panel" && els.current.body) els.current.body.scrollTop = 0;
    reveal(els.current[headingKey(step)]);
  }, [reveal, surface]);

  const prefetch = useCallback(() => loadCatalog(lang), [lang]);

  const chooseIntent = useCallback((id: IntentId) => {
    dispatch({ type: "intent", id });
    if (CATALOG_INTENTS.includes(id)) loadCatalog(lang);
  }, [lang]);

  const focusField = useCallback((key: "name" | "email" | "phone" | "note") => reveal(els.current[key]), [reveal]);

  /** «Preferisco scrivere»: niente argomento da scegliere, subito alla nota. */
  const writeInstead = useCallback(() => {
    flushSync(() => {
      dispatch({ type: "intent", id: "other" });
      dispatch({ type: "goTo", step: 3 });
    });
    reveal(els.current.note);
  }, [reveal]);

  const next = useCallback(() => {
    const d = state.draft;
    if (state.step === 1) {
      const e = validateTopic(d);
      if (hasErrors(e)) {
        flushSync(() => dispatch({ type: "errors", errors: { ...state.errors, ...e } }));
        reveal(els.current.firstIntent);
        return;
      }
      if (d.intent && CATALOG_INTENTS.includes(d.intent)) loadCatalog(lang);
      const seq = stepsFor(d.intent);
      const back = state.editReturn && d.intent === editIntentRef.current;
      goTo(back ? 3 : seq[1]);
      return;
    }
    if (state.step === 2) {
      const e = validateDetails(d, todayIso);
      if (hasErrors(e)) {
        flushSync(() => dispatch({ type: "errors", errors: { ...state.errors, ...e } }));
        reveal(els.current.date);
        return;
      }
      goTo(3);
    }
  }, [state, lang, goTo, reveal, todayIso]);

  const back = useCallback(() => {
    const seq = stepsFor(state.draft.intent);
    const i = seq.indexOf(state.step);
    goTo(seq[Math.max(0, i - 1)] ?? 1);
  }, [state.draft.intent, state.step, goTo]);

  const goToEdit = useCallback((step: StepId, edit?: boolean) => {
    if (edit) editIntentRef.current = state.draft.intent;
    goTo(step, edit);
  }, [goTo, state.draft.intent]);

  const applyContext = useCallback((ctx: OpenContext | null) => {
    if (!ctx) return;
    dispatch({ type: "context", ctx });
    if (ctx.items.length) loadCatalog(lang);
  }, [lang]);

  /** Focus all'apertura del pannello: l'avviso del carrello in sospeso, l'esito o il titolo del passo. */
  const focusCurrent = useCallback(() => {
    const el = state.pendingCart && els.current.pending
      ? els.current.pending
      : state.phase === "success" ? els.current.success : els.current[headingKey(state.step)];
    el?.focus({ preventScroll: true });
  }, [state.phase, state.step, state.pendingCart]);

  const mergeCart = useCallback(() => {
    flushSync(() => dispatch({ type: "mergeCart" }));
    loadCatalog(lang);
    reveal(els.current[headingKey(state.step)]);
  }, [lang, reveal, state.step]);

  const dismissCart = useCallback(() => {
    flushSync(() => dispatch({ type: "dismissCart" }));
    reveal(els.current[headingKey(state.step)]);
  }, [reveal, state.step]);

  const fieldBlur = useCallback((field: "email" | "phone") => (e: FocusEvent<HTMLInputElement>) => {
    if (state.attempted) return; // dopo il tentativo gli errori sono già dal vivo
    const v = e.currentTarget.value.trim();
    const bad = v !== "" && !(field === "email" ? isValidEmail(v) : isValidPhone(v));
    if (bad === !!state.errors[field]) return;
    const nextErrors = { ...state.errors };
    if (bad) nextErrors[field] = true;
    else delete nextErrors[field];
    dispatch({ type: "errors", errors: nextErrors });
  }, [state.attempted, state.errors]);

  /** Scrittura in un campo: l'errore sparisce appena il valore è corretto (non al blur). */
  const changeField = useCallback((field: FieldName, value: string) => {
    dispatch({ type: "field", field, value });
    if (state.attempted || !state.errors[field as "email" | "phone"]) return;
    const v = value.trim();
    const ok = field === "email" ? isValidEmail(v) : field === "phone" ? isValidPhone(v) : true;
    if (!ok) return;
    const nextErrors = { ...state.errors };
    delete nextErrors[field as "email" | "phone"];
    dispatch({ type: "errors", errors: nextErrors });
  }, [state.attempted, state.errors]);

  /** Tutti gli errori che bloccano l'invio, nell'ordine in cui si correggono. */
  const blocking = useCallback((): FieldErrors => ({
    ...validateDetails(state.draft, todayIso),
    ...validateFinal(finalInput),
  }), [state.draft, todayIso, finalInput]);

  const showErrors = useCallback((e: FieldErrors) => {
    if (e.date) {
      flushSync(() => {
        dispatch({ type: "errors", errors: e, attempted: true });
        dispatch({ type: "goTo", step: 2 });
      });
      reveal(els.current.date);
      return;
    }
    flushSync(() => {
      dispatch({ type: "errors", errors: e, attempted: true });
      if (state.step !== 3) dispatch({ type: "goTo", step: 3 });
    });
    const order: Array<"note" | "name" | "email" | "phone"> = ["note", "name", "email", "phone"];
    const failing = order.filter((k) => e[k]);
    if (failing.length >= 2 && els.current.summary) reveal(els.current.summary);
    else if (failing[0]) reveal(els.current[failing[0]]);
  }, [reveal, state.step]);

  const onSubmitClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    if (sendingRef.current) {
      e.preventDefault();
      return;
    }
    const errs = blocking();
    // Il submit nativo parte solo con dati validi: niente form_submit «a vuoto»
    // contato da GA4 Enhanced Measurement.
    if (hasErrors(errs)) {
      e.preventDefault();
      showErrors(errs);
    }
  }, [blocking, showErrors]);

  const onSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sendingRef.current) return;
    const errs = blocking();
    if (hasErrors(errs)) {
      showErrors(errs);
      return;
    }
    sendingRef.current = true;
    const d = state.draft;
    const snapshot = {
      message: composed.message,
      headline: composed.headline,
      email: emailValue.trim(),
      name: d.name.trim(),
      company: d.company.trim(),
      phone: d.phone.trim(),
      stamps,
      sentAt: new Date().toISOString(),
    };
    dispatch({ type: "sending" });
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), SEND_TIMEOUT_MS);
    try {
      await submitContactRequest(
        {
          name: d.name,
          company: d.company,
          email: emailValue,
          phone: d.phone,
          message: composed.message,
          marketingConsent: d.marketingConsent,
          source,
          locale,
        },
        { signal: ctrl.signal },
      );
      ga4.formSubmit(source, gaLocation); // solo dopo il 2xx
      if (!mountedRef.current) return;
      flushSync(() => dispatch({ type: "sent", snapshot }));
      // L'esito è più corto del modulo: il titolo si porta al centro, mai sotto l'header.
      const title = els.current.success;
      if (title) reveal(title, "center");
    } catch {
      // Nessun timer: l'avviso resta finché non si riprova. Dati, scelte e consenso restano.
      if (mountedRef.current) dispatch({ type: "failed" });
    } finally {
      clearTimeout(timer);
      sendingRef.current = false;
    }
  }, [blocking, showErrors, state.draft, composed.message, composed.headline, emailValue, stamps, source, locale, gaLocation, reveal]);

  const onFormKeyDown = useCallback((e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    const el = e.target;
    if (!(el instanceof HTMLInputElement)) return; // textarea: a capo; pulsanti: comportamento nativo
    // Mai un invio implicito: nei passi intermedi Invio = «Continua».
    e.preventDefault();
    if (state.step !== 3) {
      next();
      return;
    }
    const i = CONTACT_ORDER.findIndex((k) => els.current[k] === el);
    const target = i >= 0 ? CONTACT_ORDER.slice(i + 1).map((k) => els.current[k]).find(Boolean) : els.current.submit;
    target?.focus();
  }, [state.step, next]);

  const dismissSuccess = useCallback(() => {
    flushSync(() => dispatch({ type: "dismissSuccess" }));
    reveal(els.current.h1);
  }, [reveal]);

  const retry = useCallback(() => retryCatalog(lang), [lang]);

  return {
    opts, lang, t, state, dispatch, steps, stepNumber, catalog, composed, emailValue, authEmail, missing, errors,
    stamps, showOfferQtyBand, season, todayIso, sending: state.phase === "sending",
    setEl, chooseIntent, writeInstead, next, back, goTo: goToEdit, prefetch, retryCatalog: retry, applyContext,
    focusCurrent, fieldBlur, changeField, onFormKeyDown, onSubmitClick, onSubmit, focusField, dismissSuccess,
    mergeCart, dismissCart,
  };
}

export type { FieldName };
